/**
 * Minimal OKLCH <-> sRGB conversion, used to derive a full surface palette from a
 * single brand colour. See derive.ts for the palette rules that consume this.
 */

export type Oklch = { l: number; c: number; h: number };

const srgbToLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
const linearToSrgb = (c: number) => (c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055);

const clamp01 = (n: number) => (n < 0 ? 0 : n > 1 ? 1 : n);

export function hexToRgb(hex: string): [number, number, number] {
  let h = hex.replace("#", "").trim();
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  return [
    parseInt(h.slice(0, 2), 16) / 255,
    parseInt(h.slice(2, 4), 16) / 255,
    parseInt(h.slice(4, 6), 16) / 255,
  ];
}

export function rgbToHex(r: number, g: number, b: number): string {
  const hx = (n: number) => Math.round(clamp01(n) * 255).toString(16).padStart(2, "0");
  return `#${hx(r)}${hx(g)}${hx(b)}`;
}

export function hexToOklch(hex: string): Oklch {
  const [lr, lg, lb] = hexToRgb(hex).map(srgbToLinear);
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  let H = (Math.atan2(B, A) * 180) / Math.PI;
  if (H < 0) H += 360;
  return { l: L, c: Math.sqrt(A * A + B * B), h: H };
}

/** Linear-light sRGB for an OKLCH triple; components may fall outside 0..1 when out of gamut. */
function oklchToLinearRgb({ l: L, c: C, h: H }: Oklch): [number, number, number] {
  const rad = (H * Math.PI) / 180;
  const A = C * Math.cos(rad);
  const B = C * Math.sin(rad);
  const l = Math.pow(L + 0.3963377774 * A + 0.2158037573 * B, 3);
  const m = Math.pow(L - 0.1055613458 * A - 0.0638541728 * B, 3);
  const s = Math.pow(L - 0.0894841775 * A - 1.291485548 * B, 3);
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

const inGamut = ([r, g, b]: [number, number, number]) => {
  const e = 0.0001;
  return r >= -e && r <= 1 + e && g >= -e && g <= 1 + e && b >= -e && b <= 1 + e;
};

/**
 * Convert to a displayable hex, reducing chroma (never lightness) until the colour
 * fits sRGB. Holding lightness is what keeps a derived accent's contrast predictable.
 */
export function oklchToHex(colour: Oklch): string {
  if (inGamut(oklchToLinearRgb(colour))) {
    const [r, g, b] = oklchToLinearRgb(colour).map(linearToSrgb) as [number, number, number];
    return rgbToHex(r, g, b);
  }
  let lo = 0;
  let hi = colour.c;
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    if (inGamut(oklchToLinearRgb({ ...colour, c: mid }))) lo = mid;
    else hi = mid;
  }
  const [r, g, b] = oklchToLinearRgb({ ...colour, c: lo }).map(linearToSrgb) as [number, number, number];
  return rgbToHex(r, g, b);
}

/** Relative luminance (WCAG), for the contrast assertions in the theme tests. */
export function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map(srgbToLinear);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Flatten `over` under `colour` at `alpha`, so tinted surfaces can be measured. */
export function flatten(colour: string, alpha: number, over: string): string {
  const c = hexToRgb(colour);
  const o = hexToRgb(over);
  return rgbToHex(
    c[0] * alpha + o[0] * (1 - alpha),
    c[1] * alpha + o[1] * (1 - alpha),
    c[2] * alpha + o[2] * (1 - alpha)
  );
}
